import {
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { ExistingAuftraegeSelectionComponent } from '../consolidation-page/components/existing-auftraege-selection/existing-auftraege-selection.component';
import { FieldGroupExtractionViewComponent } from '../../domains/data-capturing/field-group-extraction-view-component/field-group-extraction-view.component';
import { OcrFormInputDirective } from '../../directives/ocr-form-input.directive';
import { PersistDataService } from '../../service/app-state/persist-data.service';
import {
  ct2ConfigurationByBatchDomain,
  DropdownFieldModel,
  FieldGroupModel,
  FieldModel,
} from '../../models/extraction-models/extraction-models';
import { ExtractionField } from '../extraction-page/extraction-page.component';
import { ExtractionPage, ExtractionSection } from '../../models/domain-extraction-specifications';
import { CtBatchSource, isEingangCtBatch, isProofCtBatch } from '@ps-gui/claims-terminal-shared';
import { KeyValue } from '@angular/common';
import { CtBatchStoreService } from '../../service/app-state/ct-batch-store.service';
import { FeatureFlagService } from '@ps-gui/shared-core';
import { DropdownOption } from '@vydaps/gui-commons';
import { TenantsService } from '@ps-gui/ui';
import { ExtractedValuesByKey } from '../../models/extracted-values';
import { FormControlStatus } from '@angular/forms';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { PageNavigationService } from '../../service/app-state/page-navigation.service';
import { PrefillResults } from '../../service/prefill.service';
import { ExistingAuftraegeSearchParameters } from '../consolidation-page/components/existing-auftraege-selection/model/existing-auftraege-search-parameters.interface';
import { debounceTime } from 'rxjs';

@Component({
  selector: 'ct-extraction-auftrag-assignment',
  imports: [
    ExistingAuftraegeSelectionComponent,
    FieldGroupExtractionViewComponent,
    OcrFormInputDirective,
  ],
  templateUrl: './auftrag-assignment.component.html',
})
export class AuftragAssignmentComponent implements OnInit {
  private readonly pageNavigationService = inject(PageNavigationService);
  protected readonly persistDataService = inject(PersistDataService);
  private readonly featureFlagService = inject(FeatureFlagService);
  protected readonly ctBatchStore = inject(CtBatchStoreService);
  private readonly tenantsService = inject(TenantsService);

  readonly currentActiveFieldModel = input.required<FieldModel | undefined>();
  readonly currentActiveField = model.required<ExtractionField | undefined>();
  readonly currentActiveSection = model.required<ExtractionSection | undefined>();
  readonly extractionPage = input.required<ExtractionPage>();
  readonly areAllSectionsValid = input.required<boolean>();

  readonly changeSectionStatus = output<{
    section: ExtractionSection;
    status: FormControlStatus;
  }>();
  readonly continue = output();

  protected readonly initialFieldData = signal<PrefillResults>({
    fields: {},
    sources: {},
  });

  protected readonly existingAuftraegeSearchParameters = signal<ExistingAuftraegeSearchParameters>(
    {},
  );
  private readonly initialFormValuesProcessed = signal(false);

  protected readonly config = ct2ConfigurationByBatchDomain[this.ctBatchStore.ctBatchDomain];
  private readonly batchTypes: DropdownOption<string>[] =
    this.config.consolidation?.batchClassification?.batchTypes;

  protected readonly sections = computed(() =>
    (this.extractionPage().sections as FieldGroupModel[])
      .map((section) => {
        if (section.key === 'consolidation_auftrag') {
          return this.createPrefilledAuftragSection(section);
        }
        if (section.key === 'clerk_fields') {
          return this.createPrefilledSachbearbeiterSection(section);
        }
        return section;
      })
      .filter(
        (section) =>
          !section.visibleForSource ||
          section.visibleForSource.includes(this.ctBatchStore.ctBachSource),
      ),
  );

  protected readonly isLegacyBackend = signal(
    this.ctBatchStore.ctBachSource === CtBatchSource.LEGACY,
  );
  protected readonly legacyTenantDropdownOptions = signal(
    this.tenantsService.getTenantDropdownOptions(),
  );
  protected readonly tenantDropdownOptions = computed(() => {
    if (this.isLegacyBackend()) {
      return this.legacyTenantDropdownOptions();
    }
    return (
      this.ctBatchStore.ctBatch.metaData?.tenants.map((tenant) => ({
        value: tenant.uniqueKey,
        label: tenant.name,
      })) ?? []
    );
  });
  protected readonly selectedTenantDropdownOption = computed(() => {
    const legacy = this.isLegacyBackend();

    return this.tenantDropdownOptions().find(
      (tenantOption) =>
        tenantOption.value ===
        (legacy ? this.ctBatchStore.ctBatch.tenant : this.ctBatchStore.ctBatch.metaData?.tenant),
    );
  });

  protected readonly displayExistierendeAuftraegeSelection = computed(
    () => !this.isLegacyBackend() && this.featureFlagService.configData().ctAuftragZuordnungEnabled,
  );

  private readonly auftragAssignmentFieldValues = signal<ExtractedValuesByKey>({});

  protected readonly directValueChangeFields = new Map<string, string[][]>([
    [
      'consolidation_auftrag',
      [
        ['consolidation_auftrag', 'tenant'],
        ['consolidation_auftrag', 'auftragsart'],
        ['consolidation_auftrag', 'kopfdaten_schadennummer'],
        ['consolidation_auftrag', 'kopfdaten_kennung'],
      ],
    ],
  ]);

  constructor() {
    effect(() => {
      if (this.areAllSectionsValid()) {
        this.pageNavigationService.enableNavigation('continue');
      } else {
        this.pageNavigationService.disableNavigation(
          'continue',
          'Bitte vervollständige bzw. korrigiere deine Eingaben',
        );
      }
    });

    toObservable(this.auftragAssignmentFieldValues)
      .pipe(takeUntilDestroyed(), debounceTime(1000))
      .subscribe(() => this.writeFieldValuesToSnapshot());

    this.pageNavigationService
      .navigate$('continue')
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.continue.emit());
  }

  ngOnInit() {
    this.persistDataService.getConsolidatedDataSnapshot(0).then((data) => {
      this.initialFieldData.set({ fields: data.fields, sources: {} });
    });
  }

  private createPrefilledAuftragSection(section: FieldGroupModel): FieldGroupModel {
    const mandant = section.fieldModels[0] as DropdownFieldModel;
    mandant.options = this.tenantDropdownOptions();
    mandant.defaultValue = this.selectedTenantDropdownOption();
    mandant.disabled = this.isLegacyBackend();

    const auftragsart = section.fieldModels[1] as DropdownFieldModel;
    auftragsart.options = this.batchTypes;
    auftragsart.defaultValue = this.getDefaultAuftragsart();
    auftragsart.disabled = this.isLegacyBackend();

    const schadenNummer = section.fieldModels[3] as FieldModel;
    schadenNummer.defaultValue = this.ctBatchStore.ctBatch.metaData?.schadenNummer;

    const messageForVyda = section.fieldModels[5] as FieldModel;
    messageForVyda.defaultValue = this.ctBatchStore.ctBatch.metaData?.initialeNachricht;

    const fieldModels = this.displayExistierendeAuftraegeSelection()
      ? section.fieldModels.filter((f) => f.key !== 'eingangstyp')
      : section.fieldModels;

    return { ...section, fieldModels } as FieldGroupModel;
  }

  private createPrefilledSachbearbeiterSection(section: FieldGroupModel): FieldGroupModel {
    const metaData = this.ctBatchStore.ctBatch.metaData;

    if (!metaData) {
      return section;
    }

    const vorname = section.fieldModels[0] as FieldModel;
    vorname.defaultValue = metaData.sachbearbeiter?.vorname;

    const nachname = section.fieldModels[1] as FieldModel;
    nachname.defaultValue = metaData.sachbearbeiter?.nachname;

    const email = section.fieldModels[2] as FieldModel;
    email.defaultValue = metaData.sachbearbeiter?.email;

    return { ...section };
  }

  private getDefaultAuftragsart(): DropdownOption<string> | undefined {
    const batch = this.ctBatchStore.ctBatch;

    let value: string | undefined;

    if (isProofCtBatch(batch)) {
      value = batch.proofType;
    }

    if (isEingangCtBatch(batch)) {
      value = batch.metaData?.auftragstyp;
    }

    if (value) {
      return this.batchTypes.find((batchType) => {
        return batchType.value === value;
      });
    }

    return undefined;
  }

  protected saveFieldGroupData(values: ExtractedValuesByKey) {
    this.auftragAssignmentFieldValues.update((current) => ({
      ...current,
      ...values,
    }));
  }

  private async writeFieldValuesToSnapshot() {
    const data = await this.persistDataService.getConsolidatedDataSnapshot(0);

    data.fields = {
      ...data.fields,
      ...this.auftragAssignmentFieldValues(),
    };

    await this.persistDataService.saveConsolidatedDataSnapshot(0, data.fields, data.tables);

    if (!this.initialFormValuesProcessed()) {
      this.initialFormValuesProcessed.set(true);

      this.existingAuftraegeSearchParameters.set({
        tenant: data.fields['tenant'] as string,
        kopfdaten_schadennummer: data.fields['kopfdaten_schadennummer'] as string,
        auftragsart: data.fields['auftragsart'] as string,
        kopfdaten_kennung: data.fields['kopfdaten_kennung'] as string,
      });
    }
  }

  protected onDirectValueChanged(data: KeyValue<string, any>) {
    this.existingAuftraegeSearchParameters.update((current) => {
      current[data.key] = data.value;
      return { ...current };
    });
  }

  protected onCurrentFormFieldChange(section: ExtractionSection, field?: ExtractionField) {
    this.currentActiveSection.set(section);
    this.currentActiveField.set(field);
  }
}
